import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShieldAlert, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { api, errMsg, eur, fmtDateTime } from '../api';
import { usePortal } from '../PortalAuth';
import { PageHeader, Card, SectionTitle, Empty, goldBtn } from '../ui';
import SubjectForm from '../aml/SubjectForm';

export const runCheck = async (subjects, reportId, refresh) => {
  const { data } = await api.post('/aml/checks', { subjects, report_id: reportId || null });
  refresh?.();
  return data;
};

function Sources({ st }) {
  if (!st) return null;
  const items = [['Banca dati internazionale (PEP, sanzioni, adverse media)', st.sgr], ['Offshore Leaks', (st.offshore?.entities || 0) > 0], ['Politici locali (PIL)', true], ['Sentenze di Cassazione', true]];
  return (
    <div className="flex flex-wrap gap-2 mt-4" data-testid="aml-sources">
      {items.map(([l, on]) => <span key={l} className={`text-[11px] px-2.5 py-1 border rounded-sm ${on ? 'border-[#1F242C] text-[#C9CCD1]' : 'border-[#D9822B]/40 text-[#D9822B]'}`}>{l}{on ? '' : ' · in preparazione'}</span>)}
    </div>
  );
}

export default function ReputationalPage() {
  const navigate = useNavigate();
  const { refresh } = usePortal();
  const [subjects, setSubjects] = useState([]);
  const [st, setSt] = useState(null);
  const [checks, setChecks] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/aml/status').then((r) => setSt(r.data)).catch(() => {});
    api.get('/aml/checks').then((r) => setChecks(r.data)).catch((e) => { toast.error(errMsg(e)); setChecks([]); });
  }, []);

  const start = async () => {
    setBusy(true);
    try { const c = await runCheck(subjects, null, refresh); navigate(`/portal/reputational/${c.id}`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader kicker="AML · Antiriciclaggio" title="Reputational check" />
      <Card className="p-6 mb-8">
        <SectionTitle right={<span className="text-xs font-mono text-[#5A5F67]" data-testid="aml-price">{st ? `${eur(st.price_cents)} per soggetto` : ''}</span>}>Nuovo controllo</SectionTitle>
        <p className="text-sm text-[#8A8F97] mb-5">Aggiungi persone o aziende da verificare. Puoi anche partire da un report: apri il report e usa "Reputational check".</p>
        <SubjectForm subjects={subjects} setSubjects={setSubjects} />
        <Sources st={st} />
        <Button onClick={start} disabled={!subjects.length || busy} className={`${goldBtn} h-11 mt-6 px-6`} data-testid="aml-start-btn">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <><ShieldAlert size={15} className="mr-2" />Avvia controllo {subjects.length ? `(${subjects.length})` : ''}</>}
        </Button>
      </Card>
      <Card>
        <div className="p-6 pb-2"><SectionTitle>Controlli eseguiti</SectionTitle></div>
        {checks && checks.length === 0 && <Empty icon={ShieldAlert} title="Nessun controllo ancora" text="I controlli eseguiti compaiono qui, con esito e PDF scaricabile." />}
        <div className="divide-y divide-[#1F242C]" data-testid="aml-check-list">
          {(checks || []).map((c) => (
            <Link key={c.id} to={`/portal/reputational/${c.id}`} className="px-6 py-4 flex items-center gap-4 hover:bg-[#D4AF37]/[0.04] transition-colors" data-testid={`aml-check-${c.id}`}>
              <div className="flex-1 min-w-0"><p className="text-sm truncate">{c.label}</p><p className="text-xs font-mono text-[#5A5F67]">{fmtDateTime(c.created_at)} · {c.subjects.length} soggetti · {c.status === 'running' ? 'in corso' : `${c.subjects.filter((s) => s.outcome === 'Riscontri presenti').length} con riscontri`}</p></div>
              {c.status === 'running' ? <Loader2 size={15} className="animate-spin text-[#D4AF37]" /> : <ArrowRight size={15} className="text-[#5A5F67]" />}
            </Link>
          ))}
        </div>
      </Card>
    </div>
  );
}
